import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, ShoppingCart, Users, Heart, Gift } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import HeroCanvas from "../components/site/HeroCanvas";
import EditorialMarquee from "../components/site/Marquee";
import ComparisonMatrix from "../components/site/ComparisonMatrix";
import EmailOptin from "../components/site/EmailOptin";
import { FadeUp, MaskedLines, Stagger, staggerItem } from "../components/site/Reveal";
import { PLATFORMS } from "../data/platforms";
import { HUBS } from "../data/hubs";
import { GUIDES } from "../data/guides";
import { HOME_FAQ } from "../data/site";

const HUB_ICONS = { ecommerce: ShoppingCart, agencies: Users, nonprofits: Heart, free: Gift };

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-40 pb-24 lg:pt-48" data-testid="hero">
      <HeroCanvas />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-volt/[0.06] blur-[120px]" />
      <motion.div style={{ y, opacity }} className="relative mx-auto max-w-6xl px-6">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="overline-label mb-6 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-volt" />
          2026 Edition · Updated July 2026 · 5 Platforms Tested Hands-On
        </motion.p>
        <h1 className="max-w-4xl font-display text-5xl font-black leading-[1.04] tracking-tighter md:text-6xl" data-testid="hero-title">
          <MaskedLines
            lines={[
              "Best Email Marketing",
              "Software for 2026:",
            ]}
          />
          <MaskedLines
            delay={0.28}
            lines={["Complete Platform Comparison"]}
            lineClassName="font-serif-accent font-medium italic text-volt"
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg"
        >
          We spent 90+ hours testing GetResponse, AWeber, MailerLite, Moosend, and ConvertKit — building real automations, measuring deliverability, and calling support — so you can pick the right platform in the next ten minutes.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a href="#top-picks" data-testid="hero-cta-picks" className="flex items-center gap-2 rounded-full bg-volt px-7 py-3.5 text-sm font-bold text-ink transition-colors hover:bg-volt-hover">
            See the Top 5 Picks <ArrowRight className="h-4 w-4" />
          </a>
          <a href="#checklist" data-testid="hero-cta-checklist" className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white/80 transition-colors hover:border-volt/50 hover:text-white">
            Get Free Email Checklist
          </a>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-16 grid max-w-2xl grid-cols-3 gap-8 border-t border-white/[0.07] pt-8"
          data-testid="hero-stats"
        >
          {[["90+", "Hours of hands-on testing"], ["$42", "Avg. return per $1 spent on email"], ["5", "Platforms fully reviewed"]].map(([n, l]) => (
            <div key={l}>
              <p className="font-display text-3xl font-extrabold text-volt">{n}</p>
              <p className="mt-1 text-xs leading-snug text-white/45">{l}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

const TopPicks = () => (
  <section id="top-picks" className="mx-auto max-w-6xl px-6 py-24" data-testid="top-picks">
    <FadeUp>
      <p className="overline-label mb-4">The Shortlist</p>
      <h2 className="max-w-2xl font-display text-4xl font-black tracking-tighter md:text-5xl">
        Five platforms.<br />Zero fluff.
      </h2>
    </FadeUp>
    <div className="mt-14 space-y-0">
      {PLATFORMS.map((p, i) => (
        <FadeUp key={p.slug} delay={i * 0.05}>
          <div className="group grid items-center gap-6 border-t border-white/[0.07] py-8 transition-colors hover:bg-white/[0.02] md:grid-cols-[80px_1fr_auto] md:gap-10 md:px-6" data-testid={`pick-${p.slug}`}>
            <span className="font-serif-accent text-5xl italic text-white/15 transition-colors group-hover:text-volt/40">0{i + 1}</span>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-8">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-display text-sm font-extrabold" style={{ backgroundColor: `${p.hue}1f`, color: p.hue }}>{p.monogram}</span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-xl font-bold">{p.name}</h3>
                  <span className="rounded-full border border-volt/40 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-volt">{p.badge}</span>
                  <span className="flex items-center gap-1 text-sm text-white/60"><Star className="h-3.5 w-3.5 fill-volt text-volt" />{p.rating}</span>
                </div>
                <p className="mt-1.5 max-w-xl text-sm text-white/50">{p.bestFor}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 md:flex-col md:items-end">
              <a href={p.affiliateUrl} target="_blank" rel="noopener noreferrer sponsored" data-testid={`pick-cta-${p.slug}`} className="flex items-center gap-1.5 rounded-full bg-volt px-5 py-2.5 text-xs font-bold text-ink transition-colors hover:bg-volt-hover">
                Try Free <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <Link to={`/reviews/${p.slug}`} data-testid={`pick-review-${p.slug}`} className="text-xs text-white/50 underline-offset-2 transition-colors hover:text-volt hover:underline">
                Full review · from {p.startingPrice}/mo
              </Link>
            </div>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const BestForCards = () => (
  <section id="best-for" className="bg-surface py-24" data-testid="best-for">
    <div className="mx-auto max-w-6xl px-6">
      <FadeUp>
        <p className="overline-label mb-4">Best-For Categories</p>
        <h2 className="max-w-2xl font-display text-4xl font-black tracking-tighter md:text-5xl">The right tool for <span className="font-serif-accent font-medium italic text-volt">your</span> business</h2>
      </FadeUp>
      <Stagger className="mt-14 grid gap-6 sm:grid-cols-2">
        {HUBS.map((h) => {
          const Icon = HUB_ICONS[h.slug];
          return (
            <motion.div key={h.slug} variants={staggerItem}>
              <Link
                to={`/best/${h.slug}`}
                data-testid={`hub-card-${h.slug}`}
                className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-white/[0.07] bg-cardink p-8 transition-transform duration-300 hover:-translate-y-1.5"
              >
                {h.image && (
                  <div className="absolute inset-0">
                    <img src={h.image} alt="" className="h-full w-full object-cover opacity-[0.14] transition-opacity duration-500 group-hover:opacity-[0.22]" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-cardink via-cardink/80 to-cardink/40" />
                  </div>
                )}
                <div className="relative">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-volt/10">
                    <Icon className="h-5 w-5 text-volt" />
                  </span>
                  <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">{h.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{h.lede.split(".")[0]}.</p>
                </div>
                <span className="relative mt-6 flex items-center gap-2 text-sm font-semibold text-volt">
                  See our picks <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </Link>
            </motion.div>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const QuizBanner = () => (
  <section className="mx-auto max-w-6xl px-6 pb-24" data-testid="quiz-banner">
    <FadeUp>
      <Link to="/quiz" data-testid="quiz-banner-link" className="group relative flex flex-col items-start justify-between gap-6 overflow-hidden rounded-2xl border border-volt/25 bg-cardink p-10 transition-transform duration-300 hover:-translate-y-1 md:flex-row md:items-center md:p-14">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-volt/[0.08] blur-[80px]" />
        <div className="relative">
          <p className="overline-label mb-3 !text-volt">60-Second Quiz</p>
          <h2 className="max-w-xl font-display text-3xl font-black tracking-tighter md:text-4xl">Not sure which one? Answer 4 questions, get your match.</h2>
        </div>
        <span className="relative flex items-center gap-2 whitespace-nowrap rounded-full bg-volt px-8 py-4 text-sm font-bold text-ink transition-colors group-hover:bg-volt-hover">
          Find My Platform <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
        </span>
      </Link>
    </FadeUp>
  </section>
);

const GuidesStrip = () => (
  <section className="mx-auto max-w-6xl px-6 py-24" data-testid="guides-strip">
    <FadeUp className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <p className="overline-label mb-4">Field Guides</p>
        <h2 className="max-w-xl font-display text-4xl font-black tracking-tighter md:text-5xl">Learn the craft, free</h2>
      </div>
      <Link to="/guides" data-testid="guides-strip-all" className="flex items-center gap-2 text-sm font-semibold text-volt hover:underline underline-offset-4">
        All 6 guides <ArrowRight className="h-4 w-4" />
      </Link>
    </FadeUp>
    <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {GUIDES.slice(0, 3).map((g, i) => (
        <motion.div key={g.slug} variants={staggerItem}>
          <Link to={`/guides/${g.slug}`} data-testid={`guides-strip-${g.slug}`} className="group flex h-full flex-col rounded-2xl border border-white/[0.07] bg-cardink p-7 transition-transform duration-300 hover:-translate-y-1">
            <span className="font-serif-accent text-3xl italic text-volt/40">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-3 flex-1 font-display text-lg font-bold leading-snug transition-colors group-hover:text-volt">{g.title}</h3>
            <p className="mt-3 text-xs text-white/40">{g.readTime}</p>
          </Link>
        </motion.div>
      ))}
    </Stagger>
  </section>
);

const OptinSection = () => (
  <section id="checklist" className="relative overflow-hidden py-28" data-testid="checklist-section">
    <div className="pointer-events-none absolute right-[-120px] top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full bg-volt/[0.07] blur-[100px]" />
    <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 lg:grid-cols-2">
      <FadeUp>
        <p className="overline-label mb-4">Free Lead Magnet</p>
        <h2 className="font-display text-4xl font-black tracking-tighter md:text-5xl">
          The 10-point <span className="font-serif-accent font-medium italic text-volt">Email Marketing</span> Checklist
        </h2>
        <p className="mt-5 max-w-lg text-base leading-relaxed text-white/60">
          The exact pre-launch checklist we run before every campaign — authentication, welcome flows, segmentation, and list hygiene. Steal it, print it, stick it on your wall.
        </p>
        <div className="mt-8">
          <EmailOptin source="checklist-home" />
        </div>
      </FadeUp>
      <FadeUp delay={0.15} className="hidden lg:block">
        <div className="glass rotate-2 rounded-2xl p-8 transition-transform duration-500 hover:rotate-0">
          <p className="font-serif-accent text-lg italic text-volt/80">Before you hit send…</p>
          <ul className="mt-5 space-y-3">
            {["SPF, DKIM & DMARC verified", "Welcome sequence live", "One clear CTA per email", "Mobile preview checked", "Inactive subs pruned"].map((t, i) => (
              <li key={i} className="flex items-center gap-3 border-b border-white/5 pb-3 text-sm text-white/70 last:border-0">
                <span className="flex h-5 w-5 items-center justify-center rounded border border-volt/40 text-[10px] text-volt">{i + 1}</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </FadeUp>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="mx-auto max-w-4xl px-6 pb-28" data-testid="faq-section">
    <FadeUp className="text-left">
      <p className="overline-label mb-4">FAQ</p>
      <h2 className="font-display text-4xl font-black tracking-tighter">Questions, answered honestly</h2>
    </FadeUp>
    <FadeUp delay={0.1} className="mt-10">
      <Accordion type="single" collapsible>
        {HOME_FAQ.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-white/[0.07]">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-base font-bold hover:text-volt hover:no-underline">
              {f.q}
            </AccordionTrigger>
            <AccordionContent data-testid={`faq-content-${i}`} className="text-sm leading-relaxed text-white/60">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </FadeUp>
  </section>
);

const HomePage = () => {
  useEffect(() => {
    document.title = "Best Email Marketing Software for 2026 | Top Email Marketing Tools";
  }, []);
  return (
    <main>
      <Hero />
      <EditorialMarquee />
      <TopPicks />
      <section className="mx-auto max-w-6xl px-6 pb-24" data-testid="matrix-section">
        <FadeUp>
          <p className="overline-label mb-4">Head-to-Head</p>
          <h2 className="mb-12 max-w-2xl font-display text-4xl font-black tracking-tighter md:text-5xl">The 2026 comparison matrix</h2>
        </FadeUp>
        <ComparisonMatrix />
        <p className="mt-4 text-xs text-white/35">Pricing for entry tiers at smallest contact band, July 2026. Vendors update pricing frequently — confirm on official sites.</p>
      </section>
      <BestForCards />
      <OptinSection />
      <FaqSection />
    </main>
  );
};

export default HomePage;
;
